import * as React from 'react';
import { ApplicationInfo } from 'library/App';
import { CustomThunkDispatch } from '../../configureStore';
import {
  INavigationItem,
  INavigationSubItem,
} from '@sportnet/ui/TheLayout/Navigation';
import { Link } from 'react-router-dom';
import { Loader } from '@sportnet/ui/Loader';
import { RootState } from '../../rootReducer';
import { __ } from '../../utilities';
import {
  aclSelector,
  activeAppspaceIdSelector,
  activeAppspaceNameSelector,
  activeAppspaceSelector,
  applicationInfoSelector,
  appsSelector,
  appspacesSelector,
  breadcrumbsSelector,
} from '../../containers/App/selectors';
import { compose } from 'redux';
import { connect, useDispatch } from 'react-redux';
import {
  setApplicationInfo,
  setApps,
  setAppspaces,
} from 'containers/App/actions';
import { useAuth } from '@sportnet/auth-react';
import TheLayout from '@sportnet/ui/TheLayout';
import TitlePanel from '@sportnet/ui/TitlePanel';
import config from '../../config';
import getProp from '@sportnet/utilities/getProp';
import sportnetApi from 'sportnetApi';
import styled from '../../theme/styled-components';

const LoaderWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

interface OwnProps {
  topFixed?: JSX.Element;
  bottomFixed?: JSX.Element;
  isLoading?: boolean | null;
  children?: React.ReactNode;
}

const mapStateToProps = (state: RootState) => ({
  appspace: activeAppspaceSelector(state),
  activeAppSpaceId: activeAppspaceIdSelector(state),
  appspaces: appspacesSelector(state),
  apps: appsSelector(state),
  organizationName: activeAppspaceNameSelector(state),
  application: applicationInfoSelector(state),
  breadcrumbs: breadcrumbsSelector(state),
  acl: aclSelector(state),
});

type IMapStateToProps = ReturnType<typeof mapStateToProps>;
type Props = OwnProps &
  IMapStateToProps & {
    dispatch: CustomThunkDispatch;
  };

interface MenuItem {
  url: string;
  label: string;
  subitems: MenuItem[];
  acl?: string;
}

const Layout: React.FC<Props> = ({
  appspace,
  activeAppSpaceId,
  application,
  breadcrumbs,
  topFixed,
  bottomFixed,
  children,
  organizationName,
  isLoading,
  apps,
  appspaces,
  acl,
}) => {
  const [sidebarVisible, setSidebarVisible] = React.useState(false);
  const [menuItems, setMenuItems] = React.useState<MenuItem[]>([]);
  const [notificationCount, setNotificationCount] = React.useState(-1);

  const { logout, authUser, accessToken, ppo } = useAuth();
  const dispatch = useDispatch();
  const changeMenuItems = React.useCallback(
    (aclObject: typeof acl) => {
      const newMenuItems = [
        {
          url: `/admin/${activeAppSpaceId || ''}/procedures`,
          label: __('Licenčné konania'),
          acl: 'getProcedures',
          subitems: [],
        },
        {
          url: `/admin/${activeAppSpaceId || ''}/instances`,
          label: __('Moje konania'),
          acl: 'meInstances',
          subitems: [],
        },
      ].reduce((acc, item) => {
        if (!item.acl || aclObject[item.acl]) {
          const { acl, ...restItem } = item;
          return [...acc, restItem];
        }
        return acc;
      }, []);
      setMenuItems(newMenuItems);
    },
    [activeAppSpaceId]
  );

  React.useEffect(() => {
    changeMenuItems(acl);
  }, [acl, changeMenuItems]);

  const getNotificationCount = React.useCallback(async () => {
    if (accessToken) {
      try {
        setNotificationCount(
          (await sportnetApi.meUnreadMessagesCount())?.count || 0
        );
      } catch (e) {
        console.error(e);
      }
    }
  }, [accessToken]);

  React.useEffect(() => {
    getNotificationCount();
    setInterval(getNotificationCount, 300000);
  }, [getNotificationCount]);

  const handleOpen = () => {
    setSidebarVisible(true);
  };

  const handleClose = () => {
    setSidebarVisible(false);
  };

  const basicCrumbs = [
    <Link key={0} to="/">
      {__('Zoznam organizácií')}
    </Link>,
    organizationName,
  ];
  const settings = {
    topFixed: (
      <div style={{ position: 'relative', zIndex: 1, backgroundColor: '#fff' }}>
        <TitlePanel
          name={appspace ? appspace.display_name || '' : ''}
          title={getProp(application, ['name'], '')}
          logo={getProp(application, ['logo'], '')}
          onLogout={logout}
          crumbs={[...basicCrumbs, ...breadcrumbs]}
          menuButton={{
            opened: sidebarVisible,
            onOpen: handleOpen,
            onClose: handleClose,
          }}
        />
        {topFixed}
      </div>
    ),
    bottomFixed: <div />,
  };
  if (bottomFixed) {
    settings.bottomFixed = bottomFixed;
  }

  React.useEffect(() => {
    const initializeApp = async () => {
      const response = await sportnetApi.meAppSpaces({ expandApp: true });

      const appsForActiveAppspace = (response?.apps || [])
        .filter((a) => (a.appspaces || []).some((as) => as.app_space === ppo))
        .map((a) => {
          const appSpaceIds = (a.appspaces || []).map((as) => as.app_space);
          return { ...a.app, appSpaceIds };
        });

      dispatch(setApps(appsForActiveAppspace));
      const appObj = (response.apps || []).find(
        (a) => a.app_id === config.APP_ID
      );

      if (appObj) {
        const newAppSpaces = appObj.appspaces || [];
        dispatch(setAppspaces(newAppSpaces));
      }

      if (!ppo) {
        return;
      }

      const appInfo = await sportnetApi.getPublicApp(config.APP_ID || '');

      if (appInfo) {
        dispatch(setApplicationInfo(appInfo as ApplicationInfo));
      }
    };

    initializeApp();
  }, [dispatch, ppo]);

  return (
    <TheLayout
      grant={authUser?.appSpace?.grant}
      admin={authUser?.appSpace?.admin}
      accessToken={accessToken}
      notificationCount={notificationCount}
      userName={authUser?.appSpace?.displayName}
      userSubName={authUser?.appSpace?.roles?.[config.APP_ID]}
      userPictureSrc={authUser?.photoUrl}
      title={getProp(application, ['name'], '')}
      top={topFixed}
      navigation={menuItems}
      renderNavigationLink={(renderProps: {
        item: INavigationItem | INavigationSubItem;
        linkProps: {
          className: string;
          onClick?: () => void;
          children: React.ReactNode;
        };
      }) => {
        const {
          item: { url },
          linkProps,
        } = renderProps;

        return (
          <Link to={url || ''} {...linkProps}>
            {linkProps.children}
          </Link>
        );
      }}
      onLogout={(ev) => {
        ev.preventDefault();
        logout();
      }}
      app={
        application
          ? {
              _id: application._id,
              appSpaceIds: [],
              name: application.name,
              url: application.url,
              logo: application.logo || undefined,
            }
          : undefined
      }
      apps={(apps || []).map((app) => ({
        _id: app._id || '',
        name: app.name || '',
        url: app.url || '',
        appSpaceUrlPattern: app.appSpaceUrlPattern || null,
        logo: app.logo || null,
        appSpaceIds: app.appSpaceIds || [],
        description: '' as const,
      }))}
      appSpace={{
        _id: appspace?.app_space || '',
        name: appspace?.org_profile?.name || '',
        logo_public_url: appspace?.org_profile?.logo_public_url || '',
        organization_id: appspace?.org_profile?.organization_id || '',
        organization_name: appspace?.org_profile?.organization_name || '',
      }}
      appSpaces={(appspaces || []).map((appspace) => {
        return {
          _id: appspace.app_space,
          name: appspace.org_profile?.name || '',
          organization_id: appspace.org_profile?._id || '',
          organization_name: appspace.org_profile?.organization_name || '',
          logo_public_url: appspace.org_profile?.logo_public_url || '',
        };
      })}
      // userOrganization={organizationName || ''}
      // userOrganizationProfile={
      //   appspace
      //     ? appspace.org_profile
      //       ? appspace.org_profile.name
      //       : ''
      //     : ''
      // }
      {...(bottomFixed && { bottom: bottomFixed })}
    >
      {isLoading ? (
        <LoaderWrapper>
          <Loader size="xl" />
        </LoaderWrapper>
      ) : (
        children
      )}
    </TheLayout>
  );
};

export default compose(connect(mapStateToProps))(Layout);
